import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import ChartFactory from "src/charts/ChartFactory";
import WidgetPlaceholder from "./WidgetPlaceholder";
import PracticeMapWidget from "./PracticeMapWidget";
import PracticeListPanel, { PA_GREEN, ABANDON_RED } from "./PracticeListPanel";
import { getTierStyle } from "src/charts/types";
import type { QuadrantPractice } from "src/charts/types";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";

// ─── Types ────────────────────────────────────────────────────────────────────

type SpecFilterOption = number | string | { value: number | string; label: string };

type SpecFilter = {
  key: string;
  control_type: "select" | "number";
  label: string;
  default: number | string;
  options?: SpecFilterOption[];
  min?: number;
  max?: number;
};

const optVal = (o: SpecFilterOption): number | string =>
  typeof o === "object" && o !== null && "value" in o ? o.value : (o as number | string);

const optLabel = (o: SpecFilterOption): string =>
  typeof o === "object" && o !== null && "label" in o ? String(o.label) : String(o);

type TierCount = { count?: number };

type KpiData = {
  total_practices?: number;
  total_pts?: number;
  overall_pa_percentage?: string;
  overall_abandon_percentage?: string;
  tier_summary?: {
    performing?: TierCount;
    steady?: TierCount;
    priority_focus?: TierCount;
    external?: TierCount;
  };
};

type KpiQuadrantInternalData = {
  practices: QuadrantPractice[];
  kpi: KpiData;
  tier_rules: Record<string, string>;
};

type Props = {
  title?: string;
  data: unknown;
  widgetId?: string | number;
  chartSpec?: Record<string, unknown>;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <div className="d-flex flex-column align-items-center px-2">
      <span
        style={{
          fontSize: 10,
          color: "var(--bs-secondary-color)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: color ?? "var(--bs-body-color)",
          lineHeight: 1.2,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function TierPill({
  label,
  count,
  hidden,
}: {
  label: string;
  count?: number;
  hidden?: boolean;
}) {
  const s = getTierStyle(label);
  return (
    <div
      className="d-flex align-items-center gap-1"
      title={hidden ? "Hidden via chart legend" : undefined}
      style={{
        borderRadius: 20,
        background: s.bg,
        border: `1px solid ${s.border}`,
        padding: "2px 10px",
        opacity: hidden ? 0.4 : 1,
        transition: "opacity 150ms ease",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: s.dot,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: s.text,
          whiteSpace: "nowrap",
          textDecoration: hidden ? "line-through" : "none",
        }}
      >
        {count ?? 0} {label}
      </span>
    </div>
  );
}

function KpiStrip({
  kpi,
  hiddenTiers,
}: {
  kpi: KpiData;
  hiddenTiers: string[];
}) {
  const ts = kpi.tier_summary ?? {};
  const isHidden = (tier: string) => hiddenTiers.includes(tier);
  return (
    <div
      className="d-flex align-items-center flex-wrap gap-2 border-bottom px-3"
      style={{ paddingTop: 6, paddingBottom: 6, flexShrink: 0 }}
    >
      <Stat label="Practices" value={kpi.total_practices ?? "—"} />
      <Stat label="Total Cases" value={kpi.total_pts ?? "—"} />
      <Stat
        label="PA Approved"
        value={kpi.overall_pa_percentage ?? "—"}
        color={PA_GREEN}
      />
      <Stat
        label="Abandoned"
        value={kpi.overall_abandon_percentage ?? "—"}
        color={ABANDON_RED}
      />
      <div
        style={{
          width: 1,
          alignSelf: "stretch",
          background: "var(--bs-border-color)",
          margin: "0 4px",
        }}
      />
      <TierPill label="Performing" count={ts.performing?.count} hidden={isHidden("Performing")} />
      <TierPill label="Steady" count={ts.steady?.count} hidden={isHidden("Steady")} />
      <TierPill label="Priority Focus" count={ts.priority_focus?.count} hidden={isHidden("Priority Focus")} />
      <TierPill label="External" count={ts.external?.count} hidden={isHidden("External")} />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function KpiQuadrantWidget({
  data: propData,
  widgetId,
  chartSpec,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: Props) {
  // Single source of truth for cross-chart selection. Every chart renders
  // from this id, and every view can write it: scatter point, map dot, or
  // list row (scatter background / Esc / "← All" clear it).
  const [selectedId, setSelectedId] = useState<number | string | null>(null);

  // Tiers toggled off via the scatter legend. Also a single source of truth:
  // the scatter hides the series, and the map + list drop those practices.
  const [hiddenTiers, setHiddenTiers] = useState<string[]>([]);

  const [draftFilters, setDraftFilters] = useState<Record<string, number | string>>({});
  const [appliedWidgetFilters, setAppliedWidgetFilters] = useState<Record<string, number | string>>({});

  const canFetch =
    !!dashboardName &&
    !!widgetTemplateId &&
    !!appliedFilters &&
    Object.keys(appliedFilters).length > 0;

  const { data: queryData, isLoading } = useQuery({
    queryKey: [
      "widget",
      dashboardName,
      widgetTemplateId,
      appliedFilters,
      appliedWidgetFilters,
    ],
    queryFn: ({ signal }) =>
      getDashboardWidgetDataApi(
        dashboardName!,
        String(widgetTemplateId!),
        { filters: appliedFilters, widget_filters: appliedWidgetFilters },
        { signal },
      ),
    enabled: canFetch,
    staleTime: 0,
    gcTime: 0,
  });

  const rawResponse = queryData as
    | { data?: unknown; kpi?: unknown; metadata?: Record<string, unknown> }
    | undefined;

  const specFilters = useMemo<SpecFilter[]>(() => {
    const fromMeta = rawResponse?.metadata?.filters;
    if (Array.isArray(fromMeta) && fromMeta.length > 0) return fromMeta as SpecFilter[];
    return (chartSpec?.filters as SpecFilter[] | undefined) ?? [];
  }, [rawResponse, chartSpec]);

  const specDefaults = useMemo(
    () => Object.fromEntries(specFilters.map((f) => [f.key, f.default])),
    [specFilters],
  );

  const draftVal = (f: SpecFilter): number | string =>
    draftFilters[f.key] ?? f.default;

  // The `canFetch` branch built a fresh object literal on every render, so the
  // useMemo below — which lists `data` as a dependency — recomputed on every
  // pass instead of only when the response changed. Memoizing the branch keeps
  // the identity stable while the inputs are unchanged.
  const data = useMemo<KpiQuadrantInternalData | null>(() => {
    if (!canFetch) return propData as KpiQuadrantInternalData | null;
    // Bound to a local first: `Array.isArray(rawResponse?.data)` narrows the
    // property, not `rawResponse` itself, so reading `rawResponse.data` in the
    // true branch is a strict-mode error ("possibly undefined").
    const practiceRows = rawResponse?.data;
    return {
      practices: (Array.isArray(practiceRows)
        ? practiceRows
        : []) as QuadrantPractice[],
      // rawResponse is already declared with `kpi?: unknown` above, so the
      // `as any` this replaces was doing nothing.
      kpi: (rawResponse?.kpi ?? {}) as KpiData,
      tier_rules: (rawResponse?.metadata?.tier_rules ?? {}) as Record<
        string,
        string
      >,
    };
  }, [canFetch, propData, rawResponse]);

  const { practices, kpi, tier_rules } = useMemo(() => {
    const d = data as KpiQuadrantInternalData | null;
    return {
      practices: d?.practices ?? [],
      kpi: (d?.kpi ?? {}) as KpiData,
      tier_rules: d?.tier_rules ?? {},
    };
  }, [data]);

  const selectedPractice = useMemo<QuadrantPractice | null>(
    () =>
      selectedId === null
        ? null
        : practices.find(
            (p) => (p.practice_id ?? p.practice) === selectedId,
          ) ?? null,
    [practices, selectedId],
  );

  // If a filter change removed the selected practice from the data set, or
  // its tier was hidden via the legend, drop the stale selection so all
  // charts return to the overview together.
  useEffect(() => {
    if (selectedId === null) return;
    if (!selectedPractice || hiddenTiers.includes(selectedPractice.tier ?? "")) {
      setSelectedId(null);
    }
  }, [selectedId, selectedPractice, hiddenTiers]);

  const handleTierToggle = useCallback((tier: string) => {
    setHiddenTiers((prev) =>
      prev.includes(tier) ? prev.filter((t) => t !== tier) : [...prev, tier],
    );
  }, []);

  // Practices remaining after legend toggles — what the map and list render.
  const visiblePractices = useMemo(
    () =>
      hiddenTiers.length === 0
        ? practices
        : practices.filter((p) => !hiddenTiers.includes(p.tier ?? "")),
    [practices, hiddenTiers],
  );

  const clearSelection = useCallback(() => setSelectedId(null), []);

  // Esc clears the selection from anywhere.
  useEffect(() => {
    if (selectedId === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") clearSelection();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedId, clearSelection]);

  // Quadrant threshold lines follow the applied widget filters (fall back to
  // the spec defaults) so the chart always draws the tiers' actual boundaries.
  const thresholds = useMemo(() => {
    const pick = (key: string): number | undefined => {
      const v = appliedWidgetFilters[key] ?? specDefaults[key];
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    return { pa: pick("pa_threshold"), abandon: pick("abandon_threshold") };
  }, [appliedWidgetFilters, specDefaults]);

  const quadrantData = useMemo(
    () => ({
      practices,
      tier_rules,
      _selectedId: selectedId,
      _thresholds: thresholds,
      _hiddenTiers: hiddenTiers,
      _onTierToggle: handleTierToggle,
    }),
    [practices, tier_rules, selectedId, thresholds, hiddenTiers, handleTierToggle],
  );

  // Shared select/toggle used by every view (scatter, map, list): selecting
  // the already-selected practice clears the selection.
  const handleSelect = useCallback((id: number | string | null) => {
    if (id === null) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => (prev === id ? null : id));
  }, []);

  const handlePointClick = useCallback(
    (point: unknown) => {
      if (!point) {
        handleSelect(null);
        return;
      }
      const p = point as QuadrantPractice;
      handleSelect(p.practice_id ?? p.practice ?? null);
    },
    [handleSelect],
  );

  if (isLoading || !data) return <WidgetPlaceholder height={480} />;

  const chartId = String(widgetId ?? "kpi-quadrant");

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      <KpiStrip kpi={kpi} hiddenTiers={hiddenTiers} />

      {specFilters.length > 0 && (
        <div
          className="d-flex align-items-center flex-wrap border-bottom px-3"
          style={{ paddingTop: 5, paddingBottom: 5, flexShrink: 0, gap: "4px 12px", background: "var(--bs-tertiary-bg, #f8fafc)" }}
        >
          {specFilters.map((f) => {
            const accent = f.key.startsWith("pa_")
              ? PA_GREEN
              : f.key.startsWith("abandon_")
              ? ABANDON_RED
              : "var(--bs-primary)";
            const val = draftVal(f);

            if (f.control_type === "select") {
              return (
                <div key={f.key} className="d-flex align-items-center gap-1">
                  <span style={{ fontSize: 11, color: "var(--bs-secondary-color)", whiteSpace: "nowrap" }}>
                    {f.label}
                  </span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "auto", fontSize: 11, minWidth: 56, padding: "1px 24px 1px 6px", cursor: "pointer" }}
                    value={String(val)}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({ ...prev, [f.key]: e.target.value }))
                    }
                  >
                    {(f.options ?? []).map((opt) => {
                      const v = optVal(opt);
                      return (
                        <option key={String(v)} value={String(v)}>{optLabel(opt)}</option>
                      );
                    })}
                  </select>
                </div>
              );
            }

            const numVal = Number(val);
            const min = f.min ?? 0;
            const max = f.max ?? 100;
            const pct = ((numVal - min) / (max - min)) * 100;
            return (
              <div key={f.key} className="d-flex align-items-center gap-1">
                <span style={{ fontSize: 11, color: accent, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {f.label}
                </span>
                <div style={{ width: 80 }}>
                  <input
                    type="range"
                    min={min}
                    max={max}
                    step={1}
                    value={numVal}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({ ...prev, [f.key]: Number(e.target.value) }))
                    }
                    style={{
                      width: "100%",
                      height: 5,
                      appearance: "none",
                      WebkitAppearance: "none",
                      background: `linear-gradient(to right, ${accent} ${pct}%, var(--bs-border-color, #dee2e6) ${pct}%)`,
                      borderRadius: 4,
                      outline: "none",
                      cursor: "pointer",
                      accentColor: accent,
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: accent,
                    minWidth: 28,
                    textAlign: "right",
                  }}
                >
                  {numVal}%
                </span>
              </div>
            );
          })}

          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={{ fontSize: 12, padding: "3px 14px", marginLeft: "auto" }}
            onClick={() => setAppliedWidgetFilters({ ...specDefaults, ...draftFilters })}
          >
            Apply
          </button>
        </div>
      )}

      <div className="d-flex flex-fill" style={{ minHeight: 0 }}>
        {/* Left column: scatter (top 50%) + map with dots (bottom 50%) */}
        <div
          className="d-flex flex-column border-end"
          style={{ width: "60%", minWidth: 0 }}
        >
          <div style={{ flex: 1, minHeight: 0 }}>
            <ChartFactory
              chartType="quadrant_scatter"
              chartId={`${chartId}-scatter`}
              title=""
              data={quadrantData}
              fillHeight
              onPointClick={handlePointClick}
            />
          </div>
          <div className="border-top" style={{ flex: 1, minHeight: 0 }}>
            <PracticeMapWidget
              practices={visiblePractices}
              selectedPractice={selectedPractice}
              chartId={`${chartId}-map`}
              onPracticeSelect={handleSelect}
            />
          </div>
        </div>

        {/* Right column: practice list, or prescriber drilldown when selected */}
        <div
          className="d-flex flex-column"
          style={{ width: "40%", minWidth: 0, minHeight: 0 }}
        >
          <PracticeListPanel
            practices={visiblePractices}
            selected={selectedPractice}
            onBack={clearSelection}
            onSelect={(p) => handleSelect(p.practice_id ?? p.practice ?? null)}
          />
        </div>
      </div>
    </div>
  );
}
